import React, {useState} from "react";
import {Box, List, ListItemButton, ListItemText, Popover, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Calendar03Icon, Cancel01Icon} from "@hugeicons/core-free-icons";
import {DateCalendar, LocalizationProvider, PickersDay} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import {QUICK_RANGES} from "../../../../components/date-range/QuickRangeMenu";

// Calendar day cell: purple range highlight, matching the registry design system's accent colour
const RangeDay = ({day, rangeStart, rangeEnd, onDayClick, onDayHover, outsideCurrentMonth, ...props}) => {
  const isStart = rangeStart && !outsideCurrentMonth && day.isSame(rangeStart, "day");
  const isEnd = rangeEnd && !outsideCurrentMonth && day.isSame(rangeEnd, "day");
  const inRange = rangeStart && rangeEnd && !outsideCurrentMonth
    && day.isAfter(rangeStart, "day") && day.isBefore(rangeEnd, "day");
  const showBg = (isStart || isEnd || inRange) && rangeStart && rangeEnd;

  return (
    <Box onMouseEnter={() => !outsideCurrentMonth && onDayHover(day)}
         sx={{position: "relative", display: "flex", justifyContent: "center"}}>
      {showBg && (
        <Box sx={{
          position: "absolute", top: "50%", transform: "translateY(-50%)", height: 36,
          left: isStart ? "50%" : 0, right: isEnd ? "50%" : 0, bgcolor: "rgba(132, 145, 247, 0.12)", zIndex: 0,
        }}/>
      )}
      <PickersDay
        {...props}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        onClick={() => !outsideCurrentMonth && onDayClick(day)}
        selected={isStart || isEnd}
        sx={{
          zIndex: 1,
          ...(isStart || isEnd
            ? {bgcolor: "#8491F7 !important", color: "#fff !important", "&:hover": {bgcolor: "#6B78E8 !important"}}
            : {"&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)"}}),
        }}
      />
    </Box>
  );
};

// Sidebar of quick ranges, flat list matching the registry filter-menu style (SelectHeadFilter / PillSelect menus)
const QuickRangeList = ({selected, onSelect, maxHeight}) => {
  let lastGroup = null;
  return (
    <List dense disablePadding sx={{
      width: 170, maxHeight, overflowY: "auto", borderRight: "1px solid #ECECF3", py: 0.75,
      "&::-webkit-scrollbar": {width: 4}, "&::-webkit-scrollbar-thumb": {bgcolor: "#E4E4EE", borderRadius: 2},
    }}>
      {QUICK_RANGES.map(({label, group, calc}) => {
        const showHeader = group !== lastGroup;
        lastGroup = group;
        return [
          showHeader && (
            <Typography key={`header-${group}`} sx={{
              display: "block", px: 1.5, pt: 1, pb: 0.5, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", color: "#B4B4C3",
            }}>
              {group}
            </Typography>
          ),
          <ListItemButton
            key={label}
            selected={selected === label}
            onClick={() => {
              const [start, end] = calc();
              onSelect(label, start, end);
            }}
            sx={{
              px: 1.5, py: 0.5, mx: 0.75, borderRadius: "8px", minHeight: 0,
              "&.Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"},
              "&.Mui-selected:hover": {bgcolor: "rgba(132, 145, 247, 0.18)"},
              "&:hover": {bgcolor: "#F4F4FA"},
            }}
          >
            <ListItemText
              primary={label}
              primaryTypographyProps={{fontSize: 12.5, fontWeight: selected === label ? 600 : 500, noWrap: true}}
            />
          </ListItemButton>,
        ];
      })}
    </List>
  );
};

// Default pill trigger (flat, purple accent once a range is picked) - matches the Panel.js PillSelect convention
const DefaultTrigger = ({label, active, onClick, allowClear, onClear}) => (
  <Box
    component={"button"}
    type={"button"}
    onClick={onClick}
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75, height: 32, px: 1.25, border: "none",
      borderRadius: "999px", cursor: "pointer", fontSize: 12.5, fontWeight: 500, fontFamily: "inherit",
      color: active ? "#4B55C9" : "#6B6B80", bgcolor: active ? "rgba(132, 145, 247, 0.12)" : "#F2F2F7",
      "&:hover": {bgcolor: active ? "rgba(132, 145, 247, 0.18)" : "#ECECF3"},
    }}
  >
    <HugeiconsIcon icon={Calendar03Icon} size={14}/>
    <Typography noWrap component={"span"} sx={{fontSize: "inherit", fontWeight: "inherit", color: "inherit"}}>
      {label}
    </Typography>
    {active && allowClear ? (
      <Box
        component={"span"} role={"button"} aria-label={"Clear date range"}
        onClick={(e) => {e.stopPropagation(); onClear();}}
        sx={{display: "flex", opacity: 0.6, "&:hover": {opacity: 1}}}
      >
        <HugeiconsIcon icon={Cancel01Icon} size={12}/>
      </Box>
    ) : (
      <HugeiconsIcon icon={ArrowDown01Icon} size={12}/>
    )}
  </Box>
);

// Date range picker with a quick-range sidebar, restyled to the registry design system (flat, rounded, purple
// accent, thin border instead of heavy shadow). Reuses the quick range presets of components/date-range/QuickRangeMenu.
// value: {label, start, end} (moment/Date, or all null when nothing is picked).
// onChange(range): range = {label, start: moment, end: moment}, or null when cleared.
// renderTrigger({label, active, onClick, onClear}): optional, to embed the picker in e.g. a table header filter
// (see ConversationsPanel's DATE & TIME column) instead of the default pill.
export const DateRangePickerMenu = ({
  value, onChange, renderTrigger, allowClear = true, placeholder = "Select date range", ariaLabel,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const startDate = value?.start ? moment(value.start) : null;
  const endDate = value?.end ? moment(value.end) : null;
  const selectedLabel = value?.label || null;

  const handleClose = () => {
    setAnchorEl(null);
    setSelecting(false);
    setAnchor(null);
    setHoverDate(null);
  };

  const handleDayClick = (day) => {
    if (!selecting) {
      setAnchor(day.clone());
      setSelecting(true);
    } else {
      const [start, end] = anchor.isBefore(day) ? [anchor, day] : [day, anchor];
      const s = start.clone().startOf("day");
      const e = end.clone().endOf("day");
      onChange?.({label: `${s.format("DD MMM YYYY")} — ${e.format("DD MMM YYYY")}`, start: s, end: e});
      handleClose();
    }
  };

  const handleQuickSelect = (label, start, end) => {
    onChange?.({label, start: moment(start), end: moment(end)});
    handleClose();
  };

  const handleClear = () => {
    onChange?.(null);
    handleClose();
  };

  let rangeStart;
  let rangeEnd;
  if (selecting && anchor && hoverDate) {
    [rangeStart, rangeEnd] = anchor.isBefore(hoverDate) ? [anchor, hoverDate] : [hoverDate, anchor];
  } else if (selecting && anchor) {
    rangeStart = anchor;
    rangeEnd = null;
  } else {
    rangeStart = startDate;
    rangeEnd = endDate;
  }

  const label = startDate && endDate
    ? (selectedLabel || `${startDate.format("DD MMM YYYY")} — ${endDate.format("DD MMM YYYY")}`)
    : placeholder;
  const active = Boolean(startDate && endDate);

  const trigger = renderTrigger
    ? renderTrigger({label, active, onClick: (e) => setAnchorEl(e.currentTarget), onClear: handleClear})
    : (
      <DefaultTrigger
        label={label} active={active} allowClear={allowClear}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onClear={handleClear}
      />
    );

  return (
    <>
      <Box component={"span"} aria-label={ariaLabel} sx={{display: "inline-flex", maxWidth: "100%"}}>{trigger}</Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        transitionDuration={0}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
        slotProps={{paper: {sx: {
          borderRadius: "14px", border: "1px solid #ECECF3", boxShadow: "0 8px 24px rgba(35, 38, 77, 0.08)", mt: 0.75,
        }}}}
      >
        <Stack direction={"row"}>
          <QuickRangeList
            selected={selectedLabel}
            onSelect={handleQuickSelect}
            maxHeight={340}
          />
          <Box>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={"en-gb"}>
              <DateCalendar
                value={null}
                onChange={() => {}}
                reduceAnimations
                maxDate={moment()}
                referenceDate={startDate || moment()}
                slots={{day: RangeDay}}
                slotProps={{day: () => ({rangeStart, rangeEnd, onDayClick: handleDayClick, onDayHover: setHoverDate})}}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};
